/**
 * SurfaceModeRenderer.js
 * Helper class for wallet surface mode rendering (both, grip, split modes)
 */

import { CYODesign } from "./CYODesign.js";
import { getOpaqueBounds } from "./CompositorMasks.js";

/**
 * Handles surface-specific rendering for wallet products
 * Manages independent both mode, grip mode, and split mode rendering
 */
export class SurfaceModeRenderer {
    /**
     * @param {Object} compositor - Reference to the parent Compositor instance
     */
    constructor(compositor) {
        this._compositor = compositor;
    }

    /**
     * Get layer objects from compositor
     */
    get layers() {
        return this._compositor._layerObjects;
    }

    /**
     * Get compositor dimensions
     */
    get width() {
        return this._compositor.width;
    }

    get height() {
        return this._compositor.height;
    }

    /**
     * Helper to draw a user image layer with transforms to a target context
     * @param {CanvasRenderingContext2D} targetCtx - Target context to draw to
     * @param {Object} userImageLayer - The user image layer object
     */
    drawUserImageWithTransforms(targetCtx, userImageLayer) {
        if (!userImageLayer || !userImageLayer.visible) return;

        const canvas = userImageLayer.canvas;
        const transform = userImageLayer.transform;
        const hasTransform = transform &&
            (transform.scale !== 1 || transform.rotation !== 0 ||
             transform.translateX !== 0 || transform.translateY !== 0);

        if (canvas._isExpandedCanvas) {
            const baseOffsetX = -(canvas._viewportOffsetX || 0);
            const baseOffsetY = -(canvas._viewportOffsetY || 0);

            if (hasTransform) {
                targetCtx.save();
                const pivotX = canvas._rotationPivotX ?? canvas._originalCanvasWidth / 2;
                const pivotY = canvas._rotationPivotY ?? canvas._originalCanvasHeight / 2;
                targetCtx.translate(pivotX + transform.translateX, pivotY + transform.translateY);
                targetCtx.rotate(transform.rotation * Math.PI / 180);
                targetCtx.scale(transform.scale, transform.scale);
                targetCtx.translate(-pivotX, -pivotY);
                targetCtx.drawImage(canvas, baseOffsetX, baseOffsetY);
                targetCtx.restore();
            } else {
                targetCtx.drawImage(canvas, baseOffsetX, baseOffsetY);
            }
        } else if (hasTransform) {
            // Non-expanded canvas with transforms - use visual center as pivot
            const pivotX = userImageLayer.visualCenter?.x ?? canvas.width / 2;
            const pivotY = userImageLayer.visualCenter?.y ?? canvas.height / 2;
            targetCtx.save();
            targetCtx.translate(pivotX + transform.translateX, pivotY + transform.translateY);
            targetCtx.rotate(transform.rotation * Math.PI / 180);
            targetCtx.scale(transform.scale, transform.scale);
            targetCtx.translate(-pivotX, -pivotY);
            targetCtx.drawImage(canvas, 0, 0);
            targetCtx.restore();
        } else {
            targetCtx.drawImage(canvas, 0, 0);
        }
    }

    /**
     * Helper to draw alt compositor user image with transforms
     * @param {CanvasRenderingContext2D} targetCtx - Target context to draw to
     * @param {Object} altUserImageLayer - The alt compositor's user image layer
     */
    drawAltUserImageWithTransforms(targetCtx, altUserImageLayer) {
        if (!altUserImageLayer || !altUserImageLayer.visible || !altUserImageLayer.canvas) return;

        const altCanvas = altUserImageLayer.canvas;
        const altTransform = altUserImageLayer.transform;
        const hasTransform = altTransform &&
            (altTransform.scale !== 1 || altTransform.rotation !== 0 ||
             altTransform.translateX !== 0 || altTransform.translateY !== 0);

        if (altCanvas._isExpandedCanvas && hasTransform) {
            const baseOffsetX = -(altCanvas._viewportOffsetX || 0);
            const baseOffsetY = -(altCanvas._viewportOffsetY || 0);
            const pivotX = altCanvas._rotationPivotX ?? altCanvas._originalCanvasWidth / 2;
            const pivotY = altCanvas._rotationPivotY ?? altCanvas._originalCanvasHeight / 2;
            targetCtx.save();
            targetCtx.translate(pivotX + altTransform.translateX, pivotY + altTransform.translateY);
            targetCtx.rotate(altTransform.rotation * Math.PI / 180);
            targetCtx.scale(altTransform.scale, altTransform.scale);
            targetCtx.translate(-pivotX, -pivotY);
            targetCtx.drawImage(altCanvas, baseOffsetX, baseOffsetY);
            targetCtx.restore();
        } else if (altCanvas._isExpandedCanvas) {
            const baseOffsetX = -(altCanvas._viewportOffsetX || 0);
            const baseOffsetY = -(altCanvas._viewportOffsetY || 0);
            targetCtx.drawImage(altCanvas, baseOffsetX, baseOffsetY);
        } else if (hasTransform) {
            // Non-expanded canvas with transforms - use visual center as pivot
            const pivotX = altUserImageLayer.visualCenter?.x ?? altCanvas.width / 2;
            const pivotY = altUserImageLayer.visualCenter?.y ?? altCanvas.height / 2;
            targetCtx.save();
            targetCtx.translate(pivotX + altTransform.translateX, pivotY + altTransform.translateY);
            targetCtx.rotate(altTransform.rotation * Math.PI / 180);
            targetCtx.scale(altTransform.scale, altTransform.scale);
            targetCtx.translate(-pivotX, -pivotY);
            targetCtx.drawImage(altCanvas, 0, 0);
            targetCtx.restore();
        } else {
            targetCtx.drawImage(altCanvas, 0, 0);
        }
    }

    /**
     * Scale and draw alt compositor content into grip bounds
     * @param {CanvasRenderingContext2D} targetCtx - Target context to draw to
     * @param {HTMLCanvasElement} altContentCanvas - Canvas with alt compositor content
     * @param {Object} altCompositor - The alt compositor instance
     */
    scaleAltContentToGripBounds(targetCtx, altContentCanvas, altCompositor) {
        const gripBounds = this._compositor._gripBounds;
        const altDevice = altCompositor._device;
        const safeAreaInsets = altDevice?.safeAreaInsets || [0, 0, 0, 0];
        const contentWidthRatio = 1 - safeAreaInsets[3] - safeAreaInsets[1];
        const contentHeightRatio = 1 - safeAreaInsets[0] - safeAreaInsets[2];
        const scaleX = gripBounds.width / (altCompositor.width * contentWidthRatio);
        const scaleY = gripBounds.height / (altCompositor.height * contentHeightRatio);
        const scale = Math.min(scaleX, scaleY);
        const scaledWidth = altCompositor.width * scale;
        const scaledHeight = altCompositor.height * scale;
        const contentOffsetX = safeAreaInsets[3] * scaledWidth;
        const contentOffsetY = safeAreaInsets[0] * scaledHeight;
        const offsetX = gripBounds.x + (gripBounds.width - scaledWidth * contentWidthRatio) / 2 - contentOffsetX;
        const offsetY = gripBounds.y + (gripBounds.height - scaledHeight * contentHeightRatio) / 2 - contentOffsetY;

        targetCtx.drawImage(altContentCanvas, offsetX, offsetY, scaledWidth, scaledHeight);
    }

    /**
     * Render independent both mode content
     * Each surface (wallet and grip) renders its own treatment based on placement types
     *
     * @param {CanvasRenderingContext2D} tempCtx - Temp canvas context
     * @param {Object} altCompositor - Alt compositor for grip surface
     * @returns {Object} Object with gripContent and centerContent canvases
     */
    renderIndependentBothMode(tempCtx, altCompositor) {
        const compositor = this._compositor;
        const cyoDesign = compositor._cyoDesign;

        // Get layers
        const bgLayer = this.layers["bgImage"];
        const userImageLayer = this.layers["userImage"];
        const stickersLayer = this.layers["stickers"];
        const photoStickersLayer = this.layers["photoStickers"];

        // Determine which elements have border/pattern/corner placement
        const gripPlacementTypes = [CYODesign.Placement.border, CYODesign.Placement.pattern, CYODesign.Placement.corner];
        const userImageHasGripPlacement = gripPlacementTypes.includes(cyoDesign?.subjectPlacement);
        const stickersHaveGripPlacement = cyoDesign?.stickerSet && gripPlacementTypes.includes(cyoDesign?.stickerPlacement);

        // Z-order flag: for removeBackground + center/bottom, user image should be ON TOP of stickers
        const isRemoveBgCenterOrBottom = cyoDesign?.subjectStyle === CYODesign.Style.removeBackground &&
            (cyoDesign?.subjectPlacement === CYODesign.Placement.center || cyoDesign?.subjectPlacement === CYODesign.Placement.bottom);

        // Step 1: Draw content that goes UNDER the colored overlay to tempCanvas
        // (Center-placement stickers are drawn later, on top of user image, in centerContent)
        tempCtx.clearRect(0, 0, this.width, this.height);
        if (bgLayer && bgLayer.visible) {
            tempCtx.drawImage(bgLayer.canvas, 0, 0);
        }

        // Step 2: Create wallet surface content (border/pattern/corner user image only, masked)
        // Note: Stickers are now drawn later in centerContent so they appear on top of user image
        const walletContentCanvas = document.createElement("canvas");
        walletContentCanvas.width = this.width;
        walletContentCanvas.height = this.height;
        const walletCtx = walletContentCanvas.getContext("2d", { alpha: true });

        // Draw border/pattern/corner user image for wallet (stickers drawn later in centerContent)
        if (userImageHasGripPlacement) {
            if (photoStickersLayer && photoStickersLayer.visible) {
                walletCtx.drawImage(photoStickersLayer.canvas, 0, 0);
            }
            this.drawUserImageWithTransforms(walletCtx, userImageLayer);
        }

        // Mask wallet-specific content to wallet area only
        if (userImageHasGripPlacement) {
            walletCtx.globalCompositeOperation = "destination-in";
            walletCtx.drawImage(compositor._walletGripMaskCanvas, 0, 0);
            walletCtx.globalCompositeOperation = "source-over";
        }

        // Draw masked wallet content on top of full-bleed content
        tempCtx.drawImage(walletContentCanvas, 0, 0);

        // Step 3: Create grip surface content (altCompositor layers, scaled to grip bounds)
        const gripContentCanvas = document.createElement("canvas");
        gripContentCanvas.width = this.width;
        gripContentCanvas.height = this.height;
        const gripCtx = gripContentCanvas.getContext("2d", { alpha: true });

        // Get layers from alt compositor
        const altBgLayer = altCompositor._layerObjects?.["bgImage"];
        const altUserImageLayer = altCompositor._layerObjects?.["userImage"];
        const altStickersLayer = altCompositor._layerObjects?.["stickers"];
        const altPhotoStickersLayer = altCompositor._layerObjects?.["photoStickers"];

        // Create temp canvas at altCompositor dimensions
        const altContentCanvas = document.createElement("canvas");
        altContentCanvas.width = altCompositor.width;
        altContentCanvas.height = altCompositor.height;
        const altCtx = altContentCanvas.getContext("2d", { alpha: true });

        // Draw altCompositor layers for grip surface
        // Order: background → photoStickers → userImage → stickers (stickers on top)
        if (stickersHaveGripPlacement && altBgLayer && altBgLayer.visible && altBgLayer.canvas) {
            altCtx.drawImage(altBgLayer.canvas, 0, 0);
        }
        if (userImageHasGripPlacement && altPhotoStickersLayer && altPhotoStickersLayer.visible && altPhotoStickersLayer.canvas) {
            altCtx.drawImage(altPhotoStickersLayer.canvas, 0, 0);
        }
        if (userImageHasGripPlacement) {
            this.drawAltUserImageWithTransforms(altCtx, altUserImageLayer);
        }
        // Draw stickers last so they appear on top of user image
        // Skip stickers on grip when subjectStyle is "full" (Full Image mode)
        const isFullImageStyle = cyoDesign?.subjectStyle === CYODesign.Style.full;
        if (stickersHaveGripPlacement && !isFullImageStyle && altStickersLayer && altStickersLayer.visible && altStickersLayer.canvas) {
            altCtx.drawImage(altStickersLayer.canvas, 0, 0);
        }

        // For full/center placement: draw wallet's background on grip surface
        if (!stickersHaveGripPlacement) {
            if (bgLayer && bgLayer.visible) {
                gripCtx.drawImage(bgLayer.canvas, 0, 0);
            }
        }

        // Helper to draw center-placement user image on grip
        const drawCenterUserImageOnGrip = () => {
            if (!userImageHasGripPlacement) {
                this.drawUserImageWithTransforms(gripCtx, userImageLayer);
            }
        };

        // Helper to draw altCompositor content (stickers) scaled to grip bounds
        const drawAltContentOnGrip = () => {
            this.scaleAltContentToGripBounds(gripCtx, altContentCanvas, altCompositor);
        };

        // Helper to draw center-placement stickers on grip
        const drawCenterStickersOnGrip = () => {
            if (!stickersHaveGripPlacement && !isFullImageStyle && stickersLayer && stickersLayer.visible) {
                gripCtx.drawImage(stickersLayer.canvas, 0, 0);
            }
        };

        // Draw in correct z-order based on style/placement
        // For removeBackground + center/bottom: user image ON TOP of stickers
        if (isRemoveBgCenterOrBottom) {
            drawAltContentOnGrip();
            drawCenterStickersOnGrip();
            drawCenterUserImageOnGrip();
        } else {
            drawCenterUserImageOnGrip();
            drawAltContentOnGrip();
            drawCenterStickersOnGrip();
        }

        // Mask grip content to grip area only
        gripCtx.globalCompositeOperation = "destination-in";
        gripCtx.drawImage(compositor._invertedWalletGripMaskCanvas, 0, 0);
        gripCtx.globalCompositeOperation = "source-over";

        // Create center content for drawing after colored overlay
        // Z-order depends on subjectStyle and placement:
        // - removeBackground + center/bottom: stickers UNDER user image
        // - otherwise: stickers ON TOP of user image
        let centerContent = null;
        const hasCenterUserImage = !userImageHasGripPlacement && (userImageLayer?.visible || photoStickersLayer?.visible);
        const hasStickers = stickersLayer && stickersLayer.visible;

        if (hasCenterUserImage || hasStickers) {
            const centerUserImageCanvas = document.createElement("canvas");
            centerUserImageCanvas.width = this.width;
            centerUserImageCanvas.height = this.height;
            const centerCtx = centerUserImageCanvas.getContext("2d", { alpha: true });

            // Helper to draw stickers (with masking if needed)
            const drawStickers = () => {
                if (!hasStickers) return;
                if (stickersHaveGripPlacement) {
                    // Grip-placement stickers: mask to wallet area only
                    const maskedStickers = document.createElement("canvas");
                    maskedStickers.width = this.width;
                    maskedStickers.height = this.height;
                    const maskedCtx = maskedStickers.getContext("2d", { alpha: true });
                    maskedCtx.drawImage(stickersLayer.canvas, 0, 0);
                    maskedCtx.globalCompositeOperation = "destination-in";
                    maskedCtx.drawImage(compositor._walletGripMaskCanvas, 0, 0);
                    maskedCtx.globalCompositeOperation = "source-over";
                    centerCtx.drawImage(maskedStickers, 0, 0);
                } else {
                    // Center-placement stickers: full bleed
                    centerCtx.drawImage(stickersLayer.canvas, 0, 0);
                }
            };

            // Helper to draw user image
            const drawUserImage = () => {
                if (!hasCenterUserImage) return;
                if (photoStickersLayer && photoStickersLayer.visible) {
                    centerCtx.drawImage(photoStickersLayer.canvas, 0, 0);
                }
                this.drawUserImageWithTransforms(centerCtx, userImageLayer);
            };

            // Draw in correct z-order based on style/placement
            if (isRemoveBgCenterOrBottom) {
                drawStickers();
                drawUserImage();
            } else {
                drawUserImage();
                drawStickers();
            }

            // Apply product mask
            const maskLayer = this.layers["mask"];
            if (maskLayer && !cyoDesign.debugOptions.hideMask) {
                centerCtx.globalCompositeOperation = "destination-out";
                centerCtx.drawImage(maskLayer.canvas, 0, 0);
                centerCtx.globalCompositeOperation = "source-over";
            }

            centerContent = centerUserImageCanvas;
        }

        return {
            gripContent: gripContentCanvas,
            centerContent: centerContent
        };
    }

    /**
     * Render grip or split mode content
     *
     * @param {CanvasRenderingContext2D} tempCtx - Temp canvas context
     * @param {Object} altCompositor - Alt compositor for grip surface (optional)
     * @param {string} walletPrintSurface - Current surface mode ("grip" or "split")
     * @returns {Object} Object with gripContent canvas (null if drawn directly to tempCtx)
     */
    renderGripOrSplitMode(tempCtx, altCompositor, walletPrintSurface) {
        const compositor = this._compositor;
        const userImageLayer = this.layers["userImage"];
        const stickersLayer = this.layers["stickers"];
        const bgLayer = this.layers["bgImage"];

        // Create grip content canvas
        const gripContentCanvas = document.createElement("canvas");
        gripContentCanvas.width = this.width;
        gripContentCanvas.height = this.height;
        const gripCtx = gripContentCanvas.getContext("2d", { alpha: true });

        // Use alt compositor's user image if available
        if (altCompositor && compositor._gripBounds) {
            const altBgLayer = altCompositor._layerObjects?.["bgImage"];
            const altUserImageLayer = altCompositor._layerObjects?.["userImage"];
            const altStickersLayer = altCompositor._layerObjects?.["stickers"];
            const altPhotoStickersLayer = altCompositor._layerObjects?.["photoStickers"];

            // Create temp canvas at altCompositor dimensions
            const altContentCanvas = document.createElement("canvas");
            altContentCanvas.width = altCompositor.width;
            altContentCanvas.height = altCompositor.height;
            const altCtx = altContentCanvas.getContext("2d", { alpha: true });

            // Draw background (in grip mode only)
            if (walletPrintSurface === "grip" && altBgLayer && altBgLayer.visible && altBgLayer.canvas) {
                altCtx.drawImage(altBgLayer.canvas, 0, 0);
            }

            // Draw altCompositor's userImage with transforms
            this.drawAltUserImageWithTransforms(altCtx, altUserImageLayer);

            // photoStickers go on grip for both modes
            if (altPhotoStickersLayer && altPhotoStickersLayer.visible && altPhotoStickersLayer.canvas) {
                altCtx.drawImage(altPhotoStickersLayer.canvas, 0, 0);
            }

            // In "grip" mode: regular stickers also go on grip
            if (walletPrintSurface === "grip" && altStickersLayer && altStickersLayer.visible && altStickersLayer.canvas) {
                altCtx.drawImage(altStickersLayer.canvas, 0, 0);
            }

            // Scale and center into gripBounds
            this.scaleAltContentToGripBounds(gripCtx, altContentCanvas, altCompositor);

        } else if (userImageLayer && userImageLayer.visible && compositor._gripBounds) {
            // Fallback: scale user image to fit grip bounds
            this.renderUserImageFallback(gripCtx, userImageLayer);
        }

        // Mask grip content to grip area only
        gripCtx.globalCompositeOperation = "destination-in";
        gripCtx.drawImage(compositor._invertedWalletGripMaskCanvas, 0, 0);
        gripCtx.globalCompositeOperation = "source-over";

        // Clear tempCanvas and composite
        tempCtx.clearRect(0, 0, this.width, this.height);

        if (walletPrintSurface === "split" && compositor._walletGripMaskCanvas) {
            // SPLIT mode: Background masked to wallet area, stickers across entire product
            if (bgLayer && bgLayer.visible) {
                if (compositor._gripCanvas) {
                    // When split rendering with grip canvas, draw full bg (visible through translucent grip)
                    tempCtx.drawImage(bgLayer.canvas, 0, 0);
                } else {
                    // Normal split mode: mask bg to wallet area only
                    const walletBgCanvas = document.createElement("canvas");
                    walletBgCanvas.width = this.width;
                    walletBgCanvas.height = this.height;
                    const walletBgCtx = walletBgCanvas.getContext("2d", { alpha: true });
                    walletBgCtx.drawImage(bgLayer.canvas, 0, 0);
                    walletBgCtx.globalCompositeOperation = "destination-in";
                    walletBgCtx.drawImage(compositor._walletGripMaskCanvas, 0, 0);
                    walletBgCtx.globalCompositeOperation = "source-over";
                    tempCtx.drawImage(walletBgCanvas, 0, 0);
                }
            }

            // Draw stickers across entire product
            if (stickersLayer && stickersLayer.visible) {
                tempCtx.drawImage(stickersLayer.canvas, 0, 0);
            }

            // Return gripContentCanvas for drawing after colored overlay
            return { gripContent: gripContentCanvas };
        } else {
            // For "grip" mode: draw grip content now
            tempCtx.drawImage(gripContentCanvas, 0, 0);
            return { gripContent: null };
        }
    }

    /**
     * Fallback rendering when no altCompositor is available
     * Scales user image to fit grip bounds
     */
    renderUserImageFallback(gripCtx, userImageLayer) {
        const gripBounds = this._compositor._gripBounds;
        const userCanvas = userImageLayer.canvas;
        const contentBounds = getOpaqueBounds(userCanvas);

        if (contentBounds) {
            const srcWidth = contentBounds.width;
            const srcHeight = contentBounds.height;
            const contentCenterX = contentBounds.x + contentBounds.width / 2;
            const contentCenterY = contentBounds.y + contentBounds.height / 2;

            const padding = 0.9;
            const scaleX = (gripBounds.width * padding) / srcWidth;
            const scaleY = (gripBounds.height * padding) / srcHeight;
            const fitScale = Math.min(scaleX, scaleY);

            const gripCenterX = gripBounds.x + gripBounds.width / 2;
            const gripCenterY = gripBounds.y + gripBounds.height / 2;

            gripCtx.save();
            const userTransform = userImageLayer.transform;
            const userScale = userTransform?.scale ?? 1;
            const userRotation = userTransform?.rotation ?? 0;
            const userTranslateX = userTransform?.translateX ?? 0;
            const userTranslateY = userTransform?.translateY ?? 0;

            gripCtx.translate(gripCenterX + userTranslateX, gripCenterY + userTranslateY);
            gripCtx.rotate(userRotation * Math.PI / 180);
            gripCtx.scale(fitScale * userScale, fitScale * userScale);
            gripCtx.translate(-contentCenterX, -contentCenterY);

            if (userCanvas._isExpandedCanvas) {
                const baseOffsetX = -(userCanvas._viewportOffsetX || 0);
                const baseOffsetY = -(userCanvas._viewportOffsetY || 0);
                gripCtx.drawImage(userCanvas, baseOffsetX, baseOffsetY);
            } else {
                gripCtx.drawImage(userCanvas, 0, 0);
            }
            gripCtx.restore();
        }
    }
}
